// Cloudflare Turnstile — invisible visitor verification for AI tool calls.
// Returns a one-time token (valid 5 min) or "" if verification could not run.
const SITE_KEY = "0x4AAAAAAE0vOO-HJ7iUGZKT";
let scriptPromise = null;

function loadScript() {
  if (typeof window === "undefined") return Promise.reject(new Error("no-window"));
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      s.async = true;
      s.defer = true;
      s.onload = () => resolve();
      s.onerror = () => { scriptPromise = null; reject(new Error("turnstile-load-failed")); };
      document.head.appendChild(s);
    });
  }
  return scriptPromise;
}

export async function getTurnstileToken(action = "ai") {
  try {
    await loadScript();
    return await new Promise((resolve) => {
      let host = document.getElementById("tda-turnstile");
      if (!host) {
        host = document.createElement("div");
        host.id = "tda-turnstile";
        host.style.cssText = "position:fixed;bottom:12px;right:12px;z-index:2147483000;";
        document.body.appendChild(host);
      }
      host.innerHTML = "";
      let widgetId = null;
      const finish = (token) => {
        clearTimeout(timer);
        resolve(token || "");
        setTimeout(() => { try { if (widgetId !== null) window.turnstile.remove(widgetId); } catch (_) {} }, 800);
      };
      const timer = setTimeout(() => finish(""), 20000);
      widgetId = window.turnstile.render(host, {
        sitekey: SITE_KEY,
        action: String(action).replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 32),
        appearance: "interaction-only",
        theme: "light",
        callback: (token) => finish(token),
        "error-callback": () => finish(""),
        "expired-callback": () => {},
        "timeout-callback": () => finish(""),
      });
    });
  } catch (_) {
    return "";
  }
}

// Visible "verify you're human" check for the AI tools. Shows a branded panel
// with an interactive Turnstile challenge and resolves with a one-time token,
// or "" if the visitor cancels or the challenge cannot run. Lead forms keep
// using the invisible getTurnstileToken above.
export function requestHumanCheck(action = "ai") {
  return new Promise((resolve) => {
    let settled = false;
    let widgetId = null;
    let cleanup = () => {};

    const finish = (token) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(token || "");
    };

    loadScript().then(() => {
      const overlay = document.createElement("div");
      overlay.setAttribute("role", "dialog");
      overlay.setAttribute("aria-modal", "true");
      overlay.setAttribute("aria-labelledby", "tda-human-check-title");
      overlay.style.cssText = "position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;padding:16px;background:rgba(15,23,42,.55);";

      const card = document.createElement("div");
      card.style.cssText = "width:100%;max-width:380px;background:#fff;border-radius:16px;padding:28px;box-shadow:0 24px 60px rgba(15,23,42,.3);font-family:'Segoe UI',-apple-system,BlinkMacSystemFont,sans-serif;text-align:left;";
      card.innerHTML = [
        '<h2 id="tda-human-check-title" style="margin:0 0 8px;font-size:19px;font-weight:600;color:#0f172a;">Verify you\'re human</h2>',
        '<p style="margin:0 0 18px;font-size:14px;line-height:1.5;color:#475569;">A quick check keeps this tool available for real patients.</p>',
        '<div data-turnstile-host></div>',
        '<button type="button" data-turnstile-cancel style="margin-top:16px;background:none;border:0;padding:0;font:inherit;font-size:14px;color:#64748b;text-decoration:underline;cursor:pointer;">Cancel</button>',
      ].join("");

      overlay.appendChild(card);
      document.body.appendChild(overlay);

      const host = card.querySelector("[data-turnstile-host]");
      const cancelButton = card.querySelector("[data-turnstile-cancel]");
      const onKeyDown = (event) => { if (event.key === "Escape") finish(""); };
      const timer = setTimeout(() => finish(""), 120000);

      cleanup = () => {
        clearTimeout(timer);
        document.removeEventListener("keydown", onKeyDown);
        try { if (widgetId !== null && window.turnstile) window.turnstile.remove(widgetId); } catch (_) {}
        overlay.remove();
      };
      document.addEventListener("keydown", onKeyDown);
      cancelButton.addEventListener("click", () => finish(""));

      try {
        widgetId = window.turnstile.render(host, {
          sitekey: SITE_KEY,
          action: String(action).replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 32),
          appearance: "always",
          theme: "light",
          callback: (token) => finish(token),
          "error-callback": () => finish(""),
          "expired-callback": () => finish(""),
          "timeout-callback": () => finish(""),
        });
      } catch (_) {
        finish("");
      }
    }).catch(() => finish(""));
  });
}